/**
 * useNotificationCenter — web hook for the notification center.
 *
 * Queries listNotifications, exposes unread count, mark-read actions.
 * Same pattern as the mobile hook.
 */
import { useState, useEffect, useCallback } from 'react';
import { generateClient } from 'aws-amplify/api';
import type { GraphQLResult } from 'aws-amplify/api';

const client = generateClient();

const LIST_NOTIFICATIONS = /* GraphQL */ `
  query ListNotifications($limit: Int) {
    listNotifications(limit: $limit) {
      items {
        id
        cognitoSub
        type
        actorName
        actorCognitoSub
        preview
        deepLinkTarget
        readAt
        createdAt
      }
      unreadCount
      nextToken
    }
  }
`;

const MARK_READ = /* GraphQL */ `
  mutation MarkNotificationRead($id: ID!) {
    markNotificationRead(id: $id) {
      id
      readAt
    }
  }
`;

const MARK_ALL_READ = /* GraphQL */ `
  mutation MarkAllNotificationsRead {
    markAllNotificationsRead
  }
`;

export interface NotificationItem {
  id: string;
  cognitoSub: string;
  type: string;
  actorName: string;
  actorCognitoSub: string;
  preview: string;
  deepLinkTarget: string;
  readAt: string | null;
  createdAt: string;
}

export interface UseNotificationCenterResult {
  notifications: NotificationItem[];
  unreadCount: number;
  loading: boolean;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  refresh: () => Promise<void>;
}

export function useNotificationCenter(isAuthenticated: boolean): UseNotificationCenterResult {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated) {
      setNotifications([]);
      setUnreadCount(0);
      setLoading(false);
      return;
    }
    try {
      const res = (await client.graphql({
        query: LIST_NOTIFICATIONS,
        variables: { limit: 30 },
      })) as GraphQLResult<{
        listNotifications: { items: NotificationItem[]; unreadCount: number };
      }>;
      setNotifications(res.data?.listNotifications?.items ?? []);
      setUnreadCount(res.data?.listNotifications?.unreadCount ?? 0);
    } catch (err) {
      console.warn('[NotificationCenter] fetch failed:', err);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchNotifications();
    // Poll every 30 seconds for new notifications (until subscription is wired)
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const markRead = useCallback(async (id: string) => {
    // The DynamoDB sort key is createdAt, not id. Find the item's
    // createdAt to pass as the key to the resolver.
    const item = notifications.find((n) => n.id === id);
    const key = item?.createdAt || id;
    try {
      await client.graphql({ query: MARK_READ, variables: { id: key } });
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, readAt: new Date().toISOString() } : n)),
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.warn('[NotificationCenter] markRead failed:', err);
    }
  }, [notifications]);

  const markAllRead = useCallback(async () => {
    try {
      await client.graphql({ query: MARK_ALL_READ });
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, readAt: n.readAt || new Date().toISOString() })),
      );
      setUnreadCount(0);
    } catch (err) {
      console.warn('[NotificationCenter] markAllRead failed:', err);
    }
  }, []);

  return {
    notifications,
    unreadCount,
    loading,
    markRead,
    markAllRead,
    refresh: fetchNotifications,
  };
}
